// ====================================================================
// AUTO-GENERATED FILE - DO NOT EDIT DIRECTLY
// ====================================================================
// Source: functions/krida-patra-helpers.js
// Run "npm run sync-helpers" after modifying the source file
// Generated: 2026-04-10T03:32:21.104Z
// ====================================================================

/**
 * krida-patra-helpers.js - Shared utilities for Krida Patra card game
 *
 * This module provides shared utilities used by both the Firebase Cloud Functions
 * (server-side) and the browser frontend (client-side). It contains:
 *
 * Card Utilities:
 * - Card value calculations (numeric and point values)
 * - Deck creation and shuffling
 * - Card comparison and sorting functions
 *
 * Game Logic Helpers:
 * - Pile value computation
 * - Stockpile and harvest validation
 * - Board vulnerability checking (for AI players)
 *
 * State Management:
 * - PatraGameState class for managing game state
 *
 * Email Utilities:
 * - Email canonicalization for player matching
 *
 * Constants:
 * - SUITS, VALUES, SUIT_SYMBOLS
 * - CARDS_PER_ROUND, CARDS_PER_GAME
 * - VISIBLE_CARDS_FIRST_TURN, BONUS_MOST_CARDS
 *
 * @module krida-patra-helpers
 * @see patra/PatraGame for the main game logic class
 * @see patra/validation for additional validation functions
 */

// Card utilities
export const SUITS = ['hearts', 'diamonds', 'clubs', 'spades'];
export const VALUES = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
export const SUIT_SYMBOLS = {
  'hearts': '♥',
  'diamonds': '♦',
  'clubs': '♣',
  'spades': '♠'
};

// Game constants
export const CARDS_PER_ROUND = 12;
export const CARDS_PER_GAME = 52;
export const VISIBLE_CARDS_FIRST_TURN = 3;
export const BONUS_MOST_CARDS = 2;

/**
 * Get the numeric value of a card (for game mechanics)
 * @param {Object} card - Card object with suit and value properties
 * @returns {number} Numeric value of the card
 */
export function getCardNumericValue(card) {
  if (!card || !card.value) return 0;
  
  const faceValues = {
    'A': 1,
    'J': 11,
    'Q': 12,
    'K': 13
  };
  
  if (card.value in faceValues) {
    return faceValues[card.value];
  }
  return parseInt(card.value);
}

/**
 * Get the point value of a card
 * @param {Object} card - Card object with suit and value properties
 * @returns {number} Point value of the card
 */
export function getCardPointValue(card) {
  if (!card || !card.suit || !card.value) return 0;

  // Rule: All Aces (regardless of suit) count as 1 point.
  if (card.value === 'A') return 1;

  // Rule: Spades Count as Face Value
  if (card.suit === 'spades') {
    if (card.value === 'K') return 13;
    if (card.value === 'Q') return 12;
    if (card.value === 'J') return 11;
    // For number cards 2-10 of Spades, their face value.
    // Ace of Spades is already handled (1 point).
    const numValue = parseInt(card.value, 10);
    if (!isNaN(numValue) && numValue >= 2 && numValue <= 10) {
      return numValue;
    }
    return 0; // Should not happen if card value is valid Spade A, 2-10, J, Q, K
  }

  // Rule: Queen of Hearts counts as 2 points.
  if (card.suit === 'hearts' && card.value === 'Q') return 2;
  
  // All other non-Spade, non-Ace, non-Queen of Hearts cards are 0 points.
  return 0;
}

/**
 * Determines if the object is a card
 * @param {*} element - Element to check
 * @returns {boolean} True if the element is a card
 */
export function isCard(element) {
  return element && element.hasOwnProperty('suit') && element.hasOwnProperty('value');
}

/**
 * Compute the pile value for stockpile matching
 * @param {Array|Object} pile - Array of cards in the pile or pile object with visible/hidden arrays
 * @returns {number} The value of the pile for stockpile comparisons
 */
export function computePileValue(pile) {
  // Handle pile structure with visible/hidden arrays
  if (pile && pile.visible && Array.isArray(pile.visible)) {
    if (pile.visible.length === 0) return 0;
    // Sum all visible cards in the pile
    return pile.visible.reduce((sum, card) => sum + getCardNumericValue(card), 0);
  }
  
  // Fallback for direct array of cards (old format)
  if (!pile || !Array.isArray(pile) || pile.length === 0) return 0;
  
  // Sum all cards in the pile
  return pile.reduce((sum, card) => sum + getCardNumericValue(card), 0);
}

/**
 * Helper function to get all possible combinations (subsets) of piles
 * @param {Array<Object>} piles - Array of pile objects
 * @returns {Array<Array<Object>>} Array of all possible combinations of piles
 */
export function getAllCombinations(piles) {
  const result = [];
  const f = function(prefix, piles, start) {
    for (let i = start; i < piles.length; i++) {
      result.push([...prefix, piles[i]]);
      f([...prefix, piles[i]], piles, i + 1);
    }
  }
  f([], piles, 0);
  return result;
}

/**
 * Find valid stockpile options for a card and selected piles
 * @param {Object} state - Game state object
 * @param {number} cardIndex - Index of selected card
 * @param {Array<number>} pileIndices - Array of selected pile indices
 * @param {boolean} singlePileSumVariant - If true, only allow summing with a single pile
 * @returns {Array<Object>} Array of valid stockpile options
 */
export function findValidStockpileOptions(state, cardIndex, pileIndices, singlePileSumVariant = false) {
  // Basic validation
  if (!state || !state.board || !Array.isArray(state.board)) {
    console.error("findValidStockpileOptions: Invalid state or board");
    return [];
  }

  // Get player's hand based on current player
  const playerHand = state.currentPlayer === 0 ? state.player1Hand : state.player2Hand;
  if (!playerHand || !Array.isArray(playerHand) || cardIndex < 0 || cardIndex >= playerHand.length) {
    console.error("findValidStockpileOptions: Invalid player hand or card index");
    return [];
  }

  // Get played card and its value
  const playedCard = playerHand[cardIndex];
  const playedCardValue = getCardNumericValue(playedCard);
  
  // Basic pile validation
  if (!pileIndices || !Array.isArray(pileIndices) || pileIndices.length === 0) {
    console.error("findValidStockpileOptions: No pile indices provided");
    return [];
  }

  console.log(`KridaPatraHelpers.findValidStockpileOptions: Card value ${playedCardValue}, piles ${JSON.stringify(pileIndices)}, singlePileSumVariant: ${singlePileSumVariant}`);
  
  // Get the remaining cards in hand (excluding the played card)
  const remainingHand = [...playerHand];
  remainingHand.splice(cardIndex, 1); // Remove the played card
  
  // Get the values of the remaining cards
  const remainingHandValues = remainingHand.map(card => getCardNumericValue(card));
  console.log("KridaPatraHelpers.findValidStockpileOptions: Remaining card values in hand:", remainingHandValues);
  
  // Array to store valid options
  const validOptions = [];
  
  // Get valid pile objects and their values
  const selectedPiles = [];
  for (const index of pileIndices) {
    if (index >= 0 && index < state.board.length) {
      const pile = state.board[index];
      const pileValue = computePileValue(pile);
      selectedPiles.push({ index, value: pileValue });
      console.log(`KridaPatraHelpers.findValidStockpileOptions: Pile ${index} has value ${pileValue}`);
    }
  }

  // If no valid piles found, return empty array
  if (selectedPiles.length === 0) {
    return [];
  }
  
  // --- New Logic based on User's "Match" and "Sum" Rules --- 

  // Generate all possible non-empty combinations of the selected piles
  // Note: singlePileSumVariant is ignored here as the rules require checking all combinations.
  const pileCombinations = getAllCombinations(selectedPiles);

  // Iterate through each combination to check against Match and Sum rules
  for (const combination of pileCombinations) {
    // This shouldn't happen with getAllCombinations, but safe check
    if (combination.length === 0) continue; 

    // Calculate the sum of the piles in the current combination
    const pilesSum = combination.reduce((sum, pile) => sum + pile.value, 0);
    
    // Get the piles that were selected but are NOT in the current combination
    const remainingPiles = selectedPiles.filter(pile => 
      !combination.some(comboItem => comboItem.index === pile.index)
    );
    
    // Get the indices for this combination
    const comboIndices = combination.map(pile => pile.index);
    const remainingIndices = remainingPiles.map(pile => pile.index);

    // --- Check Rule 1: "Match" Case --- 
    // a. value(played_card) == sum(combination)
    // b. value(each_remaining_pile) == value(played_card)
    // c. value(card_in_hand) == value(played_card)
    const matchCaseConditionA = playedCardValue === pilesSum;
    const matchCaseConditionB = remainingPiles.every(pile => pile.value === playedCardValue);
    const matchCaseConditionC = remainingHandValues.includes(playedCardValue);

    if (matchCaseConditionA && matchCaseConditionB && matchCaseConditionC) {
      validOptions.push({
        targetValue: playedCardValue, // The value being matched
        type: 'match', // Rule 1
        sumIndices: comboIndices, // Piles used in the sum that equals played card
        matchIndices: remainingIndices // Piles that directly match the played card
      });
      console.log(`KridaPatraHelpers.findValidStockpileOptions: Found valid 'Match' option. Played: ${playedCardValue}. Sum Piles (${comboIndices.join(', ')}) = ${pilesSum}. Match Piles (${remainingIndices.join(', ')}) = ${playedCardValue}. Hand has ${playedCardValue}.`);
      // Continue to next combination as this one is handled
      continue; 
    }

    // --- Check Rule 2: "Sum" Case --- 
    // a. value(played_card) + sum(combination) == value(card_in_hand)
    // b. value(each_remaining_pile) == value(card_in_hand) (matching the target hand card)
    const sumCaseTargetValue = playedCardValue + pilesSum;
    const sumCaseConditionA = remainingHandValues.includes(sumCaseTargetValue);
    const sumCaseConditionB = remainingPiles.every(pile => pile.value === sumCaseTargetValue);

    if (sumCaseConditionA && sumCaseConditionB) {
       validOptions.push({
        targetValue: sumCaseTargetValue, // The value of the hand card being matched
        type: 'sum', // Rule 2
        sumIndices: comboIndices, // Piles summed with the played card
        matchIndices: remainingIndices // Piles that match the target hand card value
      });
      console.log(`KridaPatraHelpers.findValidStockpileOptions: Found valid 'Sum' option. Played: ${playedCardValue}. Sum Piles (${comboIndices.join(', ')}) = ${pilesSum}. Target Value: ${sumCaseTargetValue}. Match Piles (${remainingIndices.join(', ')}) = ${sumCaseTargetValue}. Hand has ${sumCaseTargetValue}.`);
      // Continue to next combination as this one is handled
      continue; 
    }
  }
  // --- End New Logic --- 

  console.log(`KridaPatraHelpers.findValidStockpileOptions result:`, validOptions);
  return validOptions;
}

/**
 * Find valid harvest options for a card and selected piles
 * @param {Object} state - Game state object
 * @param {number} cardIndex - Index of selected card
 * @param {Array<number>} pileIndices - Array of selected pile indices
 * @returns {Array<Object>} Array of valid harvest combinations that cover all selected piles
 */
export function findValidHarvestOptions(state, cardIndex, pileIndices) {
  // Basic validation
  if (!state || !state.board || !Array.isArray(state.board)) {
    console.error("findValidHarvestOptions: Invalid state or board");
    return [];
  }

  // Get player's hand based on current player
  const playerHand = state.currentPlayer === 0 ? state.player1Hand : state.player2Hand;
  if (!playerHand || !Array.isArray(playerHand) || cardIndex < 0 || cardIndex >= playerHand.length) {
    console.error("findValidHarvestOptions: Invalid player hand or card index");
    return [];
  }

  // Get played card and its value
  const playedCard = playerHand[cardIndex];
  const playedCardValue = getCardNumericValue(playedCard);
  
  // Basic pile validation
  if (!pileIndices || !Array.isArray(pileIndices) || pileIndices.length === 0) {
    console.error("findValidHarvestOptions: No pile indices provided");
    return [];
  }

  console.log(`KridaPatraHelpers.findValidHarvestOptions: Card value ${playedCardValue}, piles ${JSON.stringify(pileIndices)}`);
  
  // Get valid pile objects and their values
  const selectedPiles = [];
  for (const index of pileIndices) {
    if (index >= 0 && index < state.board.length) {
      const pile = state.board[index];
      const pileValue = computePileValue(pile);
      selectedPiles.push({ index, value: pileValue });
      console.log(`KridaPatraHelpers.findValidHarvestOptions: Pile ${index} has value ${pileValue}`);
    }
  }

  // If no valid piles found, return empty array
  if (selectedPiles.length === 0) {
    return [];
  }
  
  // Find all possible combinations of selected piles
  const allCombinations = getAllCombinations(selectedPiles);
  
  // Find combinations that sum to the played card's value
  const validCombinations = allCombinations.filter(combination => {
    const sum = combination.reduce((total, pile) => total + pile.value, 0);
    return sum === playedCardValue;
  });
  
  console.log(`KridaPatraHelpers.findValidHarvestOptions: Found ${validCombinations.length} valid combinations`);
  
  // Check if all piles can be covered by non-overlapping valid combinations
  const harvestOptions = findValidHarvestCombinations(selectedPiles, validCombinations);
  
  console.log(`KridaPatraHelpers.findValidHarvestOptions: Final result has ${harvestOptions.length} options`);
  return harvestOptions;
}

/**
 * Find valid harvest combinations that cover all selected piles without overlap
 * @param {Array<Object>} selectedPiles - Array of selected pile objects with index and value
 * @param {Array<Array<Object>>} validCombinations - Array of combinations that sum to the played card's value
 * @returns {Array<Object>} Array of valid harvest options, each containing combinations that cover all piles
 */
export function findValidHarvestCombinations(selectedPiles, validCombinations) {
  // If no valid combinations, return empty array
  if (validCombinations.length === 0) {
    return [];
  }
  
  // If there's just a single pile and it's valid, return it directly
  if (selectedPiles.length === 1 && validCombinations.some(combo => combo.length === 1)) {
    return [{
      combinations: [selectedPiles.map(pile => pile.index)]
    }];
  }
  
  // Convert to indices for easier comparison
  const selectedIndices = new Set(selectedPiles.map(pile => pile.index));
  const validCombinationIndices = validCombinations.map(combo => 
    combo.map(pile => pile.index)
  );
  
  // Helper function to check if an array of combinations covers all selected piles without overlap
  const isValidCover = (combinations) => {
    const coveredIndices = new Set();
    
    // Check for overlap
    for (const combo of combinations) {
      for (const index of combo) {
        if (coveredIndices.has(index)) {
          return false; // Overlap found
        }
        coveredIndices.add(index);
      }
    }
    
    // Check if all selected piles are covered
    return selectedIndices.size === coveredIndices.size && 
           [...selectedIndices].every(index => coveredIndices.has(index));
  };
  
  // Find all possible combinations of valid combinations
  const allPossibleCovers = [];
  
  // Helper function to recursively build combinations of valid combinations
  const buildCovers = (current, start) => {
    // Check if current cover is valid
    if (isValidCover(current)) {
      allPossibleCovers.push([...current]);
      return;
    }
    
    // If we've gone through all valid combinations, stop
    if (start >= validCombinationIndices.length) {
      return;
    }
    
    // Try adding each valid combination from start onwards
    for (let i = start; i < validCombinationIndices.length; i++) {
      current.push(validCombinationIndices[i]);
      buildCovers(current, i + 1);
      current.pop();
    }
  };
  
  // Start the recursive process
  buildCovers([], 0);
  
  // Convert to result format
  return allPossibleCovers.map(cover => ({
    combinations: cover
  }));
}

/**
 * Creates and shuffles a deck of cards
 * @returns {Array} Shuffled deck of cards
 */
export function createAndShuffleDeck() {
  let deck = [];
  for (let suit of SUITS) {
    for (let value of VALUES) {
      const card = {
        suit: suit,
        value: value,
        numericValue: getCardNumericValue({suit, value})
      };
      deck.push(card);
    }
  }
  for (let i = deck.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [deck[i], deck[j]] = [deck[j], deck[i]];
  }
  return deck;
}

/**
 * Comparison function for sorting hand: Value Desc (K=13..A=1), then Suit Desc (S>H>D>C)
 * @param {Object} a - First card
 * @param {Object} b - Second card
 * @returns {number} Comparison result for sorting
 */
export function compareCardsByValueThenSuit(a, b) {
  const valA = a.numericValue !== undefined ? a.numericValue : getCardNumericValue(a);
  const valB = b.numericValue !== undefined ? b.numericValue : getCardNumericValue(b);
  if (valA !== valB) {
    return valB - valA; // DESCENDING numeric value (K->A)
  }
  const suitOrder = { 'spades': 3, 'hearts': 2, 'diamonds': 1, 'clubs': 0 };
  const suitA = suitOrder[a.suit] !== undefined ? suitOrder[a.suit] : -1;
  const suitB = suitOrder[b.suit] !== undefined ? suitOrder[b.suit] : -1;
  return suitB - suitA; // Descending suit order
}

// --- NEW: PatraGameState Class ---
export class PatraGameState {
  constructor(initialState = {}) {
    // Player data
    this.player1Hand = initialState.player1Hand || [];
    this.player2Hand = initialState.player2Hand || [];
    this.player1Harvests = initialState.player1Harvests || []; // Store detailed harvest info
    this.player2Harvests = initialState.player2Harvests || []; // Store detailed harvest info
    this.player1Score = initialState.player1Score || 0;
    this.player2Score = initialState.player2Score || 0;
    this.player1RoundScore = initialState.player1RoundScore || 0; // Track score within the current round
    this.player2RoundScore = initialState.player2RoundScore || 0; // Track score within the current round

    // Game state
    this.board = initialState.board || []; // Array of pile objects { visible: [], hidden: [] }
    this.deck = initialState.deck || [];
    this.numCardsInDeck = initialState.numCardsInDeck !== undefined ? initialState.numCardsInDeck : this.deck.length;
    this.currentPlayer = initialState.currentPlayer !== undefined ? initialState.currentPlayer : 0; // 0 or 1
    this.selectedCardIndices = initialState.selectedCardIndices || []; // Indices in current player's hand
    this.selectedBoardPileIndices = initialState.selectedBoardPileIndices || []; // Indices on the board

    // --- Add Status Message Fields ---
    this.statusMessageLine1 = initialState.statusMessageLine1 || "";
    this.statusMessageLine2 = initialState.statusMessageLine2 || "";
    
    // Round/Game tracking
    this.round = initialState.round || 1;
    this.dealer = initialState.dealer !== undefined ? initialState.dealer : 1; // Player 1 (0) or Player 2 (1)
    this.isFirstTurn = initialState.isFirstTurn !== undefined ? initialState.isFirstTurn : true;
    this.bonusSowMode = initialState.bonusSowMode || false; // For potential future use (e.g., 'Sapu')
    this.gameOver = initialState.gameOver || false;
    this.winner = initialState.winner !== undefined ? initialState.winner : null; // null, 0, or 1

    // Options (should mirror PatraGame options)
    this.options = {
      winningScore: initialState.options?.winningScore || 150,
      darkMode: initialState.options?.darkMode || false,
      stockpileDisplayPreference: initialState.options?.stockpileDisplayPreference || 'base',
      bonusClearBoard: initialState.options?.bonusClearBoard === undefined ? 25 : initialState.options.bonusClearBoard,
      aiDifficulty: initialState.options?.aiDifficulty || 'easy'
    };
    
    // Special cards
    this.specialCardsEnabled = initialState.specialCardsEnabled || false;
    this.player1SpecialCardsAvailable = initialState.player1SpecialCardsAvailable || [];
    this.player2SpecialCardsAvailable = initialState.player2SpecialCardsAvailable || [];
    this.player1SpecialCardsUsed = initialState.player1SpecialCardsUsed || [];
    this.player2SpecialCardsUsed = initialState.player2SpecialCardsUsed || [];

    // Option proposal system state
    this.proposedOptions = initialState.proposedOptions || null;
    this.proposedBy = initialState.proposedBy !== undefined ? initialState.proposedBy : null; // Player index (0 or 1)
  }

  // Helper method to get a plain object representation
  toObject() {
    // Simple shallow copy of properties might be enough if no methods are involved
    // For deep copy, structuredClone or JSON parse/stringify could be used if needed
    return { ...this };
  }
}
// --- END: PatraGameState Class ---

// --- ADDED: Get Score for a list of cards ---
/**
 * Calculates the total score for a list of cards based on getCardPointValue.
 * @param {Array<Object>} cardListToCheck - An array of card objects.
 * @returns {number} The total score for the list of cards.
 */
export function getCardScore(cardListToCheck) {
  if (!cardListToCheck || !Array.isArray(cardListToCheck)) return 0;
  let currentScore = 0;
  for (const card of cardListToCheck) {
    currentScore += getCardPointValue(card);
  }
  return currentScore;
}
// --- END: Get Score for a list of cards ---

// --- ADDED: Calculate Harvest Score ---
/**
 * Calculates the score points awarded for a potential harvest action.
 * @param {Object} playedCard - The card object the player intends to play.
 * @param {Array<Object>} selectedPiles - An array of pile objects selected from the board.
 * @param {Array<Object>} allBoardPiles - The complete array of pile objects currently on the board.
 * @param {number} bonusClearBoardValue - The point value awarded for clearing the board.
 * @returns {number} The total score points for the harvest.
 */
export function calculateHarvestScore(playedCard, selectedPiles, allBoardPiles, bonusClearBoardValue) {
  if (!playedCard || !selectedPiles || !allBoardPiles) return 0;

  let totalScore = 0; // Initialize to 0, points will be added or overridden.
  let allHarvestedCards = [playedCard];

  // Helper to get all cards from a pile (avoids dependency on PatraGame instance method)
  const getAllCardsInPile = (pile) => {
    let allCards = [];
    if (!pile || typeof pile !== 'object' || pile === null || Array.isArray(pile)) {
      console.warn("getAllCardsInPile (helper): Received invalid pile structure.", pile);
      return allCards;
    }
    if (pile.visible && Array.isArray(pile.visible)) {
      allCards.push(...pile.visible);
    }
    if (pile.hidden && Array.isArray(pile.hidden)) {
      allCards.push(...pile.hidden);
    }
    return allCards;
  };

  selectedPiles.forEach(pile => {
    const cardsFromPile = getAllCardsInPile(pile);
    allHarvestedCards.push(...cardsFromPile);
  });

  // Calculate score from all harvested cards
  totalScore = getCardScore(allHarvestedCards);

  // Add clear board bonus if applicable
  const boardWillBeEmpty = allBoardPiles.length > 0 && selectedPiles.length === allBoardPiles.length;
  if (boardWillBeEmpty) {
    const validBonus = Number(bonusClearBoardValue) || 0;
    totalScore += validBonus;
  }

  return totalScore;
}
// --- END: Calculate Harvest Score ---

// --- ADDED: Functions for Board Vulnerability Check ---

/**
 * Finds if ONE subset of values sums to targetSum. Returns the subset or empty array.
 * @param {number[]} values - Array of numbers to search within.
 * @param {number} targetSum - The target sum.
 * @returns {number[]} The first subset found, or [] if none.
 */
export function findSubsetSum(values, targetSum) {
    function backtrack(startIndex, currentSum, currentSubset) {
        if (currentSum === targetSum) {
            return currentSubset; // Found a subset
        }
        if (currentSum > targetSum || startIndex === values.length) {
            return []; // Exceeded target or no more elements
        }
        // Try including values[startIndex]
        let resultWith = backtrack(startIndex + 1, currentSum + values[startIndex], [...currentSubset, values[startIndex]]);
        if (resultWith.length > 0) {
            return resultWith; // Found it down this path
        }
        // Try excluding values[startIndex]
        let resultWithout = backtrack(startIndex + 1, currentSum, currentSubset);
        return resultWithout;
    }
    return backtrack(0, 0, []);
}

/**
 * Recursive function to check if an array of values can be partitioned into groups summing to targetValue.
 * @param {number[]} values - Array of pile values.
 * @param {number} targetValue - The target value for each group.
 * @returns {boolean} - Whether the values can be partitioned.
 */
export function canPartitionValuesRecursive(values, targetValue) {
    if (values.length === 0) return true; // Base case: successfully partitioned everything
    if (targetValue <= 0) return false; // Prevent infinite loops for non-positive targets

    const subset = findSubsetSum(values, targetValue); // Needs findSubsetSum helper
    if (subset.length === 0) {
        return false; // Cannot find one group matching the target
    }

    const remainingValues = [...values];
    let subsetCopy = [...subset];

    // Remove elements of the found subset from the remaining values
    for (let i = remainingValues.length - 1; i >= 0; i--) {
        const valIndex = subsetCopy.indexOf(remainingValues[i]);
        if (valIndex > -1) {
             remainingValues.splice(i, 1);
             subsetCopy.splice(valIndex, 1);
        }
    }

    if(subsetCopy.length > 0) {
         console.error("canPartitionValuesRecursive: Subset elements not fully found in values array.", { subset, values });
         return false; // Inconsistency detected
    }

    // Recursively check the remaining values
    return canPartitionValuesRecursive(remainingValues, targetValue);
}

/**
 * Checks if a set of piles on the board is vulnerable to being completely
 * cleared by a single card value (1-13) through harvesting.
 * @param {Array<Object>} piles - An array of pile objects { visible: [], hidden: [] }.
 * @returns {boolean} - True if the board configuration is vulnerable, false otherwise.
 */
export function checkBoardVulnerability(piles) {
  if (!piles || piles.length === 0) {
    return false; // An empty board isn't vulnerable
  }

  const pileValues = [];
  try {
    for (const pile of piles) {
      const pileValue = computePileValue(pile); // Use existing helper
      if (isNaN(pileValue)) {
         console.warn("checkBoardVulnerability: Encountered NaN pile value.", pile);
         return false; // Cannot determine vulnerability if a value is invalid
      }
      // Vulnerability check requires partitioning, piles > 13 make this impossible for a single card value
      if (pileValue > 13) { 
           return false; 
      }
      pileValues.push(pileValue);
    }
  } catch (e) {
    console.error("checkBoardVulnerability: Error computing pile values.", e);
    return false; // Error during calculation means we can't confirm vulnerability
  }

  const totalSum = pileValues.reduce((sum, val) => sum + val, 0);
  if (totalSum === 0) return false; // Cannot harvest empty piles

  // Iterate through possible harvest card values (1 to 13)
  for (let targetValue = 1; targetValue <= 13; targetValue++) {
    // Optimization: If total sum isn't divisible by target, partitioning is impossible
    if (totalSum % targetValue !== 0) {
      continue;
    }

    // Check if the pile values can be perfectly partitioned into groups summing to targetValue
    // Assumes `canPartitionValuesRecursive` is available in this scope.
    if (canPartitionValuesRecursive([...pileValues], targetValue)) { // Use a copy
       console.log(`checkBoardVulnerability: Board IS vulnerable to value ${targetValue}`);
       return true; // Found a value that can clear the board
    }
  }

  // If no value from 1-13 could partition the piles, the board is not vulnerable
  return false;
}
// --- END: Functions for Board Vulnerability Check ---

// --- ADDED: isHarvestValid Helper ---
/**
 * Checks if a harvest action is valid given the game state.
 * @param {PatraGameState} state - The current game state object.
 * @param {number} cardIndex - Index of the card in player's hand.
 * @param {number[]} pileIndices - Indices of the piles on the board to harvest.
 * @returns {boolean} - Whether the harvest action is valid.
 */
export function isHarvestValid(state, cardIndex, pileIndices) {
    if (!state || !pileIndices || pileIndices.length === 0) {
        console.error(`isHarvestValid Helper: Invalid state or no piles selected`);
        return false;
    }

    const playerIndex = state.currentPlayer;
    const hand = playerIndex === 0 ? state.player1Hand : state.player2Hand;
    const board = state.board;

    // Check card index validity
    if (cardIndex < 0 || cardIndex >= hand.length) {
        console.error(`isHarvestValid Helper: Invalid card index ${cardIndex}`);
        return false;
    }

    // Get the card to harvest with
    const card = hand[cardIndex];

    // For first turn, make sure the played card is visible
    if (state.isFirstTurn && !card.isVisibleForTurn) {
        console.error(`isHarvestValid Helper: Cannot play hidden card on first turn`);
        return false;
    }

    // Check if all pile indices are valid
    const pileValues = [];
    for (const index of pileIndices) {
        if (index < 0 || index >= board.length) {
            console.error(`isHarvestValid Helper: Invalid board pile index ${index}`);
            return false;
        }
        // Also compute values here to avoid redundant loop in canPartition
        const pile = board[index];
        const pileValue = computePileValue(pile);
        if (isNaN(pileValue)) {
           console.error(`isHarvestValid Helper: Pile ${index} has invalid value.`);
           return false;
        }
        pileValues.push(pileValue);
    }

    // Calculate the numeric value of the harvest card
    const cardValue = getCardNumericValue(card);
    if (isNaN(cardValue)) {
        console.error(`isHarvestValid Helper: Invalid numeric value for harvest card`, card);
        return false;
    }

    // Check if selected piles can be partitioned into groups matching the card's value
    // Assumes `canPartitionValuesRecursive` helper is available.
    const isValid = canPartitionValuesRecursive([...pileValues], cardValue); // Use computed values
    // console.log(`isHarvestValid Helper check: CardValue=${cardValue}, PileIndices=${pileIndices}, Result=${isValid}`);
    return isValid;
}
// --- END: isHarvestValid Helper ---

// --- ADDED: isSowValid Helper ---
/**
 * Checks if a sow action is valid given the game state.
 * @param {PatraGameState} state - The current game state object.
 * @param {number} cardIndex - Index of the card in player's hand.
 * @returns {boolean} - Whether the sow action is valid.
 */
export function isSowValid(state, cardIndex) {
  if (!state) {
    console.error(`isSowValid Helper: Invalid state`);
    return false;
  }
  const playerIndex = state.currentPlayer;
  const hand = playerIndex === 0 ? state.player1Hand : state.player2Hand;

  // Check card index validity
  if (cardIndex < 0 || cardIndex >= hand.length) {
      console.error(`isSowValid Helper: Invalid card index ${cardIndex}`);
      return false;
  }

  // Get the card to sow
  const card = hand[cardIndex];

  // For first turn, make sure the played card is visible
  if (state.isFirstTurn && !card.isVisibleForTurn) {
      console.error(`isSowValid Helper: Cannot play hidden card on first turn`);
      return false;
  }

  return true;
}
// --- END: isSowValid Helper ---

// NEW: Email Canonicalization Function
/**
 * Converts an email address to its canonical form.
 * For Gmail: removes dots from the local part and anystring after a plus sign.
 * Converts the entire email to lowercase.
 * @param {string} email The email address to canonicalize.
 * @returns {string|null} The canonical email address, or null if input is invalid.
 */
export function getCanonicalEmail(email) {
    if (!email || typeof email !== 'string') {
        return null;
    }
    let lowerEmail = email.toLowerCase();
    const parts = lowerEmail.split('@');
    if (parts.length !== 2) {
        return lowerEmail; // Not a standard email format, return as is (lowercase)
    }
    let localPart = parts[0];
    const domain = parts[1];

    // Handle Gmail-specific canonicalization
    if (domain === 'gmail.com' || domain === 'googlemail.com') {
        // Remove anything after a plus sign
        localPart = localPart.split('+')[0];
        // Remove all dots
        localPart = localPart.replace(/\./g, '');
    }
    // For other domains, no special local part canonicalization beyond lowercase
    return `${localPart}@${domain}`;
}
// END NEW
